/**
 * ANATOMY COORDINATES REFERENCE
 * ViewBox: 0 0 100 185 (percentage-based coordinates)
 * All paths are SVG path data format
 *
 * To use these coordinates in your own system:
 * 1. Set your SVG viewBox to "0 0 100 185"
 * 2. Use these paths directly in <path d="..." /> elements
 * 3. The coordinates are relative to the viewBox dimensions
 */

// ============================================
// ANTERIOR VIEW (FRONT) - SURFACE REGIONS
// ============================================

export const frontRegions = [
   // HEAD & FACE
   {
    id: "crown",
    name: "Crown of Head",
    latinName: "Vertex",
    path: "M 40 2 L 60 2 L 60 10 L 40 10 Z",
    description: "Top of the skull, parietal bone region",
  },
  {
    id: "forehead",
    name: "Forehead",
    latinName: "Frons",
    path: "M 38 10 L 62 10 L 62 20 L 38 20 Z",
    description: "Frontal bone region, protects the frontal lobe of brain",
  },
  {
    id: "right-eye",
    name: "Right Eye",
    latinName: "Oculus dexter",
    path: "M 40 21 L 46 21 L 46 24 L 40 24 Z",
    description: "Organ of vision, contains cornea, iris, lens, and retina",
  },
  {
    id: "left-eye",
    name: "Left Eye",
    latinName: "Oculus sinister",
    path: "M 54 21 L 60 21 L 60 24 L 54 24 Z",
    description: "Organ of vision, contains cornea, iris, lens, and retina",
  },
  {
    id: "right-ear",
    name: "Right Ear",
    latinName: "Auris dextra",
    path: "M 34 20 L 38 20 L 38 28 L 34 28 Z",
    description: "External auditory organ, collects and directs sound waves",
  },
  {
    id: "left-ear",
    name: "Left Ear",
    latinName: "Auris sinistra",
    path: "M 62 20 L 66 20 L 66 28 L 62 28 Z",
    description: "External auditory organ, collects and directs sound waves",
  },
  {
    id: "nose",
    name: "Nose",
    latinName: "Nasus",
    path: "M 47 24 L 53 24 L 53 30 L 47 30 Z",
    description: "Organ of smell, warms and filters inhaled air",
  },
  {
    id: "right-cheek",
    name: "Right Cheek",
    latinName: "Bucca dextra",
    path: "M 38 24 L 46 24 L 46 32 L 38 32 Z",
    description: "Lateral facial region, contains buccinator muscle",
  },
  {
    id: "left-cheek",
    name: "Left Cheek",
    latinName: "Bucca sinistra",
    path: "M 54 24 L 62 24 L 62 32 L 54 32 Z",
    description: "Lateral facial region, contains buccinator muscle",
  },
  {
    id: "upper-lip",
    name: "Upper Lip",
    latinName: "Labium superius",
    path: "M 45 30 L 55 30 L 55 32 L 45 32 Z",
    description: "Contains orbicularis oris muscle, used in speech and eating",
  },
  {
    id: "lower-lip",
    name: "Lower Lip",
    latinName: "Labium inferius",
    path: "M 45 32 L 55 32 L 55 34 L 45 34 Z",
    description: "Contains orbicularis oris muscle, used in expression",
  },
  {
    id: "chin",
    name: "Chin",
    latinName: "Mentum",
    path: "M 43 34 L 57 34 L 57 38 L 43 38 Z",
    description: "Mental region, formed by the mandible",
  },

  // NECK
  {
    id: "anterior-neck",
    name: "Anterior Neck",
    latinName: "Regio cervicalis anterior",
    path: "M 44 38 L 56 38 L 56 48 L 44 48 Z",
    description: "Front of neck, contains thyroid, trachea, and carotid arteries",
  },
  {
    id: "right-lateral-neck",
    name: "Right Lateral Neck",
    latinName: "Regio cervicalis lateralis dextra",
    path: "M 36 38 L 44 38 L 44 48 L 36 48 Z",
    description: "Side of neck, sternocleidomastoid muscle region",
  },
  {
    id: "left-lateral-neck",
    name: "Left Lateral Neck",
    latinName: "Regio cervicalis lateralis sinistra",
    path: "M 56 38 L 64 38 L 64 48 L 56 48 Z",
    description: "Side of neck, sternocleidomastoid muscle region",
  },

  // SHOULDERS
  {
    id: "right-shoulder",
    name: "Right Shoulder",
    latinName: "Articulatio humeri dextra",
    path: "M 26 48 L 36 48 L 36 58 L 26 58 Z",
    description: "Right glenohumeral joint, ball-and-socket joint",
  },
  {
    id: "left-shoulder",
    name: "Left Shoulder",
    latinName: "Articulatio humeri sinistra",
    path: "M 64 48 L 74 48 L 74 58 L 64 58 Z",
    description: "Left glenohumeral joint, ball-and-socket joint",
  },
  {
    id: "right-clavicle",
    name: "Right Clavicle",
    latinName: "Clavicula dextra",
    path: "M 36 48 L 46 48 L 46 52 L 36 52 Z",
    description: "Right collarbone, connects sternum to shoulder",
  },
  {
    id: "left-clavicle",
    name: "Left Clavicle",
    latinName: "Clavicula sinistra",
    path: "M 54 48 L 64 48 L 64 52 L 54 52 Z",
    description: "Left collarbone, connects sternum to shoulder",
  },

  // CHEST & TORSO
  {
    id: "sternum",
    name: "Sternum",
    latinName: "Sternum",
    path: "M 46 52 L 54 52 L 54 68 L 46 68 Z",
    description: "Breastbone, protects heart and anchors ribs",
  },
  {
    id: "right-pectoral",
    name: "Right Chest",
    latinName: "Regio pectoralis dextra",
    path: "M 33 52 L 46 52 L 46 68 L 33 68 Z",
    description: "Right pectoral region, contains pectoralis major",
  },
  {
    id: "left-pectoral",
    name: "Left Chest",
    latinName: "Regio pectoralis sinistra",
    path: "M 54 52 L 67 52 L 67 68 L 54 68 Z",
    description: "Left pectoral region, contains pectoralis major",
  },
  {
    id: "right-nipple",
    name: "Right Nipple",
    latinName: "Papilla mammaria dextra",
    path: "M 39 60 Q 40 59 41 60 Q 41 61 40 62 Q 39 61 39 60 Z",
    description: "Right mammary papilla",
  },
  {
    id: "left-nipple",
    name: "Left Nipple",
    latinName: "Papilla mammaria sinistra",
    path: "M 59 60 Q 60 59 61 60 Q 61 61 60 62 Q 59 61 59 60 Z",
    description: "Left mammary papilla",
  },

  // ARMS
  {
    id: "right-upper-arm",
    name: "Right Upper Arm",
    latinName: "Brachium dextrum",
    path: "M 20 58 L 30 58 L 28 78 L 18 78 Z",
    description: "Right arm between shoulder and elbow, contains biceps and triceps",
  },
  {
    id: "left-upper-arm",
    name: "Left Upper Arm",
    latinName: "Brachium sinistrum",
    path: "M 70 58 L 80 58 L 82 78 L 72 78 Z",
    description: "Left arm between shoulder and elbow, contains biceps and triceps",
  },
  {
    id: "right-elbow",
    name: "Right Elbow",
    latinName: "Cubitus dexter",
    path: "M 18 78 L 28 78 L 27 84 L 17 84 Z",
    description: "Right elbow joint, hinge joint connecting arm to forearm",
  },
  {
    id: "left-elbow",
    name: "Left Elbow",
    latinName: "Cubitus sinister",
    path: "M 72 78 L 82 78 L 83 84 L 73 84 Z",
    description: "Left elbow joint, hinge joint connecting arm to forearm",
  },
  {
    id: "right-forearm",
    name: "Right Forearm",
    latinName: "Antebrachium dextrum",
    path: "M 17 84 L 27 84 L 24 104 L 14 104 Z",
    description: "Right forearm, contains radius and ulna",
  },
  {
    id: "left-forearm",
    name: "Left Forearm",
    latinName: "Antebrachium sinistrum",
    path: "M 73 84 L 83 84 L 86 104 L 76 104 Z",
    description: "Left forearm, contains radius and ulna",
  },
  {
    id: "right-wrist",
    name: "Right Wrist",
    latinName: "Carpus dexter",
    path: "M 14 104 L 24 104 L 23 110 L 13 110 Z",
    description: "Right wrist joint, contains 8 carpal bones",
  },
  {
    id: "left-wrist",
    name: "Left Wrist",
    latinName: "Carpus sinister",
    path: "M 76 104 L 86 104 L 87 110 L 77 110 Z",
    description: "Left wrist joint, contains 8 carpal bones",
  },
  {
    id: "right-palm",
    name: "Right Palm",
    latinName: "Palma manus dextra",
    path: "M 13 110 L 23 110 L 21 118 L 11 118 Z",
    description: "Right palm, contains metacarpal bones",
  },
  {
    id: "left-palm",
    name: "Left Palm",
    latinName: "Palma manus sinistra",
    path: "M 77 110 L 87 110 L 89 118 L 79 118 Z",
    description: "Left palm, contains metacarpal bones",
  },
  {
    id: "right-fingers",
    name: "Right Fingers",
    latinName: "Digiti manus dextrae",
    path: "M 11 118 L 21 118 L 18 128 L 8 128 Z",
    description: "Right phalanges, 14 bones in 5 fingers",
  },
  {
    id: "left-fingers",
    name: "Left Fingers",
    latinName: "Digiti manus sinistrae",
    path: "M 79 118 L 89 118 L 92 128 L 82 128 Z",
    description: "Left phalanges, 14 bones in 5 fingers",
  },

  // ABDOMEN
  {
    id: "epigastric",
    name: "Epigastric Region",
    latinName: "Regio epigastrica",
    path: "M 43 68 L 57 68 L 57 82 L 43 82 Z",
    description: "Upper central abdomen, overlies stomach and liver",
  },
  {
    id: "right-hypochondriac",
    name: "Right Hypochondriac",
    latinName: "Regio hypochondriaca dextra",
    path: "M 31 68 L 43 68 L 43 82 L 31 82 Z",
    description: "Upper right abdomen, overlies liver and gallbladder",
  },
  {
    id: "left-hypochondriac",
    name: "Left Hypochondriac",
    latinName: "Regio hypochondriaca sinistra",
    path: "M 57 68 L 69 68 L 69 82 L 57 82 Z",
    description: "Upper left abdomen, overlies spleen and stomach",
  },
  {
    id: "umbilical",
    name: "Umbilical Region",
    latinName: "Regio umbilicalis",
    path: "M 43 82 L 57 82 L 57 96 L 43 96 Z",
    description: "Central abdomen around navel, overlies small intestine",
  },
  {
    id: "navel",
    name: "Navel",
    latinName: "Umbilicus",
    path: "M 48 88 Q 50 87 52 88 Q 52 90 50 91 Q 48 90 48 88 Z",
    description: "Belly button, remnant of umbilical cord attachment",
  },
  {
    id: "right-lumbar",
    name: "Right Flank",
    latinName: "Regio lateralis dextra",
    path: "M 30 82 L 43 82 L 43 96 L 30 96 Z",
    description: "Right side of abdomen, overlies ascending colon",
  },
  {
    id: "left-lumbar",
    name: "Left Flank",
    latinName: "Regio lateralis sinistra",
    path: "M 57 82 L 70 82 L 70 96 L 57 96 Z",
    description: "Left side of abdomen, overlies descending colon",
  },
  {
    id: "hypogastric",
    name: "Hypogastric Region",
    latinName: "Regio pubica",
    path: "M 43 96 L 57 96 L 57 108 L 43 108 Z",
    description: "Lower central abdomen, overlies bladder",
  },
  {
    id: "right-iliac",
    name: "Right Iliac",
    latinName: "Regio inguinalis dextra",
    path: "M 32 96 L 43 96 L 43 108 L 32 108 Z",
    description: "Right lower abdomen, overlies cecum and appendix",
  },
  {
    id: "left-iliac",
    name: "Left Iliac",
    latinName: "Regio inguinalis sinistra",
    path: "M 57 96 L 68 96 L 68 108 L 57 108 Z",
    description: "Left lower abdomen, overlies sigmoid colon",
  },

  // PELVIS & GROIN
  {
    id: "right-groin",
    name: "Right Groin",
    latinName: "Inguen dextrum",
    path: "M 36 108 L 48 108 L 50 116 L 38 116 Z",
    description: "Right inguinal region, contains femoral vessels",
  },
  {
    id: "left-groin",
    name: "Left Groin",
    latinName: "Inguen sinistrum",
    path: "M 52 108 L 64 108 L 62 116 L 50 116 Z",
    description: "Left inguinal region, contains femoral vessels",
  },

  // THIGHS
  {
    id: "right-anterior-thigh",
    name: "Right Anterior Thigh",
    latinName: "Regio femoris anterior dextra",
    path: "M 36 116 L 50 116 L 48 146 L 34 146 Z",
    description: "Front of right thigh, contains quadriceps muscles",
  },
  {
    id: "left-anterior-thigh",
    name: "Left Anterior Thigh",
    latinName: "Regio femoris anterior sinistra",
    path: "M 50 116 L 64 116 L 66 146 L 52 146 Z",
    description: "Front of left thigh, contains quadriceps muscles",
  },

  // KNEES
  {
    id: "right-knee",
    name: "Right Knee",
    latinName: "Genu dextrum",
    path: "M 34 146 L 48 146 L 47 154 L 35 154 Z",
    description: "Right knee joint, largest joint in the body",
  },
  {
    id: "left-knee",
    name: "Left Knee",
    latinName: "Genu sinistrum",
    path: "M 52 146 L 66 146 L 65 154 L 53 154 Z",
    description: "Left knee joint, largest joint in the body",
  },
  {
    id: "right-patella",
    name: "Right Kneecap",
    latinName: "Patella dextra",
    path: "M 40 148 Q 41 147 42 148 Q 42.5 150 41 151.5 Q 39.5 150 40 148 Z",
    description: "Right kneecap, sesamoid bone in quadriceps tendon",
  },
  {
    id: "left-patella",
    name: "Left Kneecap",
    latinName: "Patella sinistra",
    path: "M 58 148 Q 59 147 60 148 Q 60.5 150 59 151.5 Q 57.5 150 58 148 Z",
    description: "Left kneecap, sesamoid bone in quadriceps tendon",
  },

  // LOWER LEGS
  {
    id: "right-shin",
    name: "Right Shin",
    latinName: "Crus dextrum anterius",
    path: "M 35 154 L 47 154 L 45 174 L 37 174 Z",
    description: "Front of right lower leg, contains tibialis anterior",
  },
  {
    id: "left-shin",
    name: "Left Shin",
    latinName: "Crus sinistrum anterius",
    path: "M 53 154 L 65 154 L 63 174 L 55 174 Z",
    description: "Front of left lower leg, contains tibialis anterior",
  },

  // ANKLES & FEET
  {
    id: "right-ankle",
    name: "Right Ankle",
    latinName: "Tarsus dexter",
    path: "M 37 174 L 45 174 L 45 179 L 37 179 Z",
    description: "Right ankle joint, connects leg to foot",
  },
  {
    id: "left-ankle",
    name: "Left Ankle",
    latinName: "Tarsus sinister",
    path: "M 55 174 L 63 174 L 63 179 L 55 179 Z",
    description: "Left ankle joint, connects leg to foot",
  },
  {
    id: "right-dorsum-foot",
    name: "Right Dorsum of Foot",
    latinName: "Dorsum pedis dextri",
    path: "M 36 179 L 46 179 L 47 183 L 35 183 Z",
    description: "Top of right foot, contains extensor tendons",
  },
  {
    id: "left-dorsum-foot",
    name: "Left Dorsum of Foot",
    latinName: "Dorsum pedis sinistri",
    path: "M 54 179 L 64 179 L 65 183 L 53 183 Z",
    description: "Top of left foot, contains extensor tendons",
  },
  {
    id: "right-toes",
    name: "Right Toes",
    latinName: "Digiti pedis dextri",
    path: "M 35 183 L 47 183 L 48 185 L 34 185 Z",
    description: "Right toe phalanges, 14 bones total",
  },
  {
    id: "left-toes",
    name: "Left Toes",
    latinName: "Digiti pedis sinistri",
    path: "M 53 183 L 65 183 L 66 185 L 52 185 Z",
    description: "Left toe phalanges, 14 bones total",
  },
];
// ============================================
// POSTERIOR VIEW (BACK) - SURFACE REGIONS
// ============================================

export const backRegions = [
  {
    id: "occipital",
    name: "Occipital Region",
    latinName: "Regio occipitalis",
    path: "M 42 1 Q 50 0 58 1 L 58 10 L 42 10 Z",
    description: "Back of skull, contains occipital bone and visual cortex",
  },
  {
    id: "right-parietal",
    name: "Right Parietal",
    latinName: "Regio parietalis dextra",
    path: "M 38 6 L 42 2 L 41 14 L 38 14 Z",
    description: "Right side of skull, parietal bone region",
  },
  {
    id: "left-parietal",
    name: "Left Parietal",
    latinName: "Regio parietalis sinistra",
    path: "M 62 6 L 58 2 L 59 14 L 62 14 Z",
    description: "Left side of skull, parietal bone region",
  },
  {
    id: "right-mastoid",
    name: "Right Mastoid",
    latinName: "Processus mastoideus dexter",
    path: "M 36 14 Q 34 16 34 20 Q 35 22 38 22 L 38 14 Z",
    description: "Right mastoid process, behind the ear",
  },
  {
    id: "left-mastoid",
    name: "Left Mastoid",
    latinName: "Processus mastoideus sinister",
    path: "M 64 14 Q 66 16 66 20 Q 65 22 62 22 L 62 14 Z",
    description: "Left mastoid process, behind the ear",
  },
  {
    id: "external-occipital-protuberance",
    name: "External Occipital Protuberance",
    latinName: "Protuberantia occipitalis externa",
    path: "M 47 16 Q 50 14 53 16 Q 53.5 18 50 19.5 Q 46.5 18 47 16 Z",
    description: "Bump at back of skull, nuchal ligament attachment",
  },

  // NECK (POSTERIOR)
  {
    id: "nape",
    name: "Nape of Neck",
    latinName: "Nucha",
    path: "M 43 22 L 57 22 L 57 32 L 43 32 Z",
    description: "Back of neck, contains cervical vertebrae",
  },
  {
    id: "right-posterior-triangle",
    name: "Right Posterior Triangle",
    latinName: "Trigonum colli posterius dextrum",
    path: "M 36 22 L 43 22 L 42 34 L 34 34 Z",
    description: "Right posterior cervical triangle",
  },
  {
    id: "left-posterior-triangle",
    name: "Left Posterior Triangle",
    latinName: "Trigonum colli posterius sinistrum",
    path: "M 64 22 L 57 22 L 58 34 L 66 34 Z",
    description: "Left posterior cervical triangle",
  },

  // SHOULDERS (POSTERIOR)
  {
    id: "right-shoulder-posterior",
    name: "Right Shoulder",
    latinName: "Articulatio humeri dextra",
    path: "M 26 34 Q 20 36 18 42 L 26 44 L 33 36 Z",
    description: "Right posterior shoulder, deltoid region",
  },
  {
    id: "left-shoulder-posterior",
    name: "Left Shoulder",
    latinName: "Articulatio humeri sinistra",
    path: "M 74 34 Q 80 36 82 42 L 74 44 L 67 36 Z",
    description: "Left posterior shoulder, deltoid region",
  },
  {
    id: "right-scapula",
    name: "Right Scapula",
    latinName: "Scapula dextra",
    path: "M 28 36 L 45 38 L 44 58 L 30 54 Z",
    description: "Right shoulder blade, triangular bone",
  },
  {
    id: "left-scapula",
    name: "Left Scapula",
    latinName: "Scapula sinistra",
    path: "M 72 36 L 55 38 L 56 58 L 70 54 Z",
    description: "Left shoulder blade, triangular bone",
  },
  {
    id: "interscapular",
    name: "Interscapular Region",
    latinName: "Regio interscapularis",
    path: "M 43 38 L 57 38 L 57 56 L 43 56 Z",
    description: "Between shoulder blades, thoracic spine region",
  },

  // ARMS (POSTERIOR)
  {
    id: "right-posterior-arm",
    name: "Right Posterior Arm",
    latinName: "Regio brachii posterior dextra",
    path: "M 18 42 L 26 44 L 24 62 L 16 60 Z",
    description: "Back of right upper arm, triceps region",
  },
  {
    id: "left-posterior-arm",
    name: "Left Posterior Arm",
    latinName: "Regio brachii posterior sinistra",
    path: "M 82 42 L 74 44 L 76 62 L 84 60 Z",
    description: "Back of left upper arm, triceps region",
  },
  {
    id: "right-olecranon",
    name: "Right Olecranon",
    latinName: "Olecranon dextrum",
    path: "M 16 60 L 24 62 L 23 66 L 15 64 Z",
    description: "Right elbow point, ulna projection",
  },
  {
    id: "left-olecranon",
    name: "Left Olecranon",
    latinName: "Olecranon sinistrum",
    path: "M 84 60 L 76 62 L 77 66 L 85 64 Z",
    description: "Left elbow point, ulna projection",
  },
  {
    id: "right-posterior-forearm",
    name: "Right Posterior Forearm",
    latinName: "Regio antebrachii posterior dextra",
    path: "M 15 64 L 23 66 L 20 82 L 12 80 Z",
    description: "Back of right forearm, extensor muscles",
  },
  {
    id: "left-posterior-forearm",
    name: "Left Posterior Forearm",
    latinName: "Regio antebrachii posterior sinistra",
    path: "M 85 64 L 77 66 L 80 82 L 88 80 Z",
    description: "Back of left forearm, extensor muscles",
  },
  {
    id: "right-dorsum-hand",
    name: "Right Dorsum of Hand",
    latinName: "Dorsum manus dextrae",
    path: "M 12 80 L 20 82 L 16 99 L 8 97 Z",
    description: "Back of right hand",
  },
  {
    id: "left-dorsum-hand",
    name: "Left Dorsum of Hand",
    latinName: "Dorsum manus sinistrae",
    path: "M 88 80 L 80 82 L 84 99 L 92 97 Z",
    description: "Back of left hand",
  },

  // BACK
  {
    id: "thoracic-spine",
    name: "Thoracic Spine",
    latinName: "Columna thoracica",
    path: "M 47 38 L 53 38 L 53 66 L 47 66 Z",
    description: "Mid-back spine, 12 thoracic vertebrae",
  },
  {
    id: "lumbar-spine",
    name: "Lumbar Spine",
    latinName: "Columna lumbalis",
    path: "M 47 66 L 53 66 L 53 82 L 47 82 Z",
    description: "Lower back spine, 5 lumbar vertebrae",
  },
  {
    id: "right-infrascapular",
    name: "Right Infrascapular",
    latinName: "Regio infrascapularis dextra",
    path: "M 30 54 L 47 56 L 47 68 L 32 68 Z",
    description: "Below right scapula, latissimus region",
  },
  {
    id: "left-infrascapular",
    name: "Left Infrascapular",
    latinName: "Regio infrascapularis sinistra",
    path: "M 70 54 L 53 56 L 53 68 L 68 68 Z",
    description: "Below left scapula, latissimus region",
  },
  {
    id: "right-lumbar-region",
    name: "Right Lumbar",
    latinName: "Regio lumbalis dextra",
    path: "M 32 68 L 47 68 L 47 82 L 34 82 Z",
    description: "Right lower back, kidney region",
  },
  {
    id: "left-lumbar-region",
    name: "Left Lumbar",
    latinName: "Regio lumbalis sinistra",
    path: "M 68 68 L 53 68 L 53 82 L 66 82 Z",
    description: "Left lower back, kidney region",
  },

  // SACRAL & GLUTEAL
  {
    id: "sacral",
    name: "Sacral Region",
    latinName: "Regio sacralis",
    path: "M 43 82 L 57 82 L 57 90 L 43 90 Z",
    description: "Sacrum area, fused vertebrae",
  },
  {
    id: "right-gluteal",
    name: "Right Gluteal",
    latinName: "Regio glutealis dextra",
    path: "M 32 82 L 50 82 L 50 98 L 34 98 Z",
    description: "Right buttock, gluteus muscles",
  },
  {
    id: "left-gluteal",
    name: "Left Gluteal",
    latinName: "Regio glutealis sinistra",
    path: "M 68 82 L 50 82 L 50 98 L 66 98 Z",
    description: "Left buttock, gluteus muscles",
  },
  {
    id: "gluteal-cleft",
    name: "Gluteal Cleft",
    latinName: "Crena ani",
    path: "M 47 90 L 53 90 L 53 98 L 47 98 Z",
    description: "Natal cleft between buttocks",
  },

  // POSTERIOR THIGHS
  {
    id: "right-posterior-thigh",
    name: "Right Posterior Thigh",
    latinName: "Regio femoris posterior dextra",
    path: "M 34 98 L 50 98 L 48 126 L 34 126 Z",
    description: "Back of right thigh, hamstring muscles",
  },
  {
    id: "left-posterior-thigh",
    name: "Left Posterior Thigh",
    latinName: "Regio femoris posterior sinistra",
    path: "M 50 98 L 66 98 L 64 126 L 52 126 Z",
    description: "Back of left thigh, hamstring muscles",
  },

  // POPLITEAL (BACK OF KNEE)
  {
    id: "right-popliteal",
    name: "Right Popliteal Fossa",
    latinName: "Fossa poplitea dextra",
    path: "M 34 126 L 48 126 L 47 134 L 35 134 Z",
    description: "Back of right knee, contains popliteal vessels",
  },
  {
    id: "left-popliteal",
    name: "Left Popliteal Fossa",
    latinName: "Fossa poplitea sinistra",
    path: "M 52 126 L 64 126 L 63 134 L 53 134 Z",
    description: "Back of left knee, contains popliteal vessels",
  },

  // CALVES
  {
    id: "right-calf",
    name: "Right Calf",
    latinName: "Sura dextra",
    path: "M 35 134 L 47 134 L 45 161 L 37 161 Z",
    description: "Right calf, gastrocnemius and soleus muscles",
  },
  {
    id: "left-calf",
    name: "Left Calf",
    latinName: "Sura sinistra",
    path: "M 53 134 L 63 134 L 61 161 L 55 161 Z",
    description: "Left calf, gastrocnemius and soleus muscles",
  },

  // ACHILLES & HEEL
  {
    id: "right-achilles",
    name: "Right Achilles Tendon",
    latinName: "Tendo calcaneus dexter",
    path: "M 38 161 L 42 161 L 42 168 L 38 168 Z",
    description: "Right Achilles tendon, connects calf to heel",
  },
  {
    id: "left-achilles",
    name: "Left Achilles Tendon",
    latinName: "Tendo calcaneus sinister",
    path: "M 58 161 L 62 161 L 62 168 L 58 168 Z",
    description: "Left Achilles tendon, connects calf to heel",
  },
  {
    id: "right-heel",
    name: "Right Heel",
    latinName: "Calx dextra",
    path: "M 36 168 L 46 168 L 47 176 L 35 176 Z",
    description: "Right heel, calcaneus bone",
  },
  {
    id: "left-heel",
    name: "Left Heel",
    latinName: "Calx sinistra",
    path: "M 54 168 L 62 168 L 63 176 L 53 176 Z",
    description: "Left heel, calcaneus bone",
  },
  {
    id: "right-sole",
    name: "Right Sole",
    latinName: "Planta pedis dextra",
    path: "M 35 176 L 47 176 L 48 185 L 34 185 Z",
    description: "Right sole of foot",
  },
  {
    id: "left-sole",
    name: "Left Sole",
    latinName: "Planta pedis sinistra",
    path: "M 53 176 L 63 176 L 64 185 L 52 185 Z",
    description: "Left sole of foot",
  },
];
